// Клиент `/api/v1/reviewer_checklist`.
//
// Управление per-(org, repo) чек-листом готовности reviewer'а из UI
// «Настройки → Чек-лист готовности». Зеркалит pydantic-схемы backend'а
// (`ChecklistItemResponse` / `AddChecklistItemRequest`). JWT прикручивает
// `authedFetch`; org берётся из токена (RLS), не из тела.
//
// Список отдаётся голым массивом, без обёртки `{items}`. Пункт адресуется
// непрозрачным `id` (UUID), не позицией: `DELETE /reviewer_checklist/{id}`
// без query. Неизвестный/чужой `id` — ошибка клиента (404), в отличие от
// `http/tool_lists.py`, где удаление идемпотентно.

import { authedFetch } from "../../auth/authedFetch";

// HTTP-код, который секция трактует как «пункт уже отсутствует» (гонка).
export const HTTP_NOT_FOUND = 404;

// Источник пункта: `manual` (правка человеком) или `self_improvement`
// (применение предложения `checklist_tweak`). Держим типом для Badge.
export type ChecklistItemSource = "manual" | "self_improvement";

export interface ChecklistItem {
  readonly id: string;
  readonly repo_full_name: string;
  readonly position: number;
  readonly text: string;
  readonly source: ChecklistItemSource;
}

export interface AddChecklistItemRequest {
  readonly repo_full_name: string;
  readonly text: string;
}

/**
 * HTTP-ошибка с числовым `status`. Секция различает 404 (пункта уже нет)
 * от прочих кодов без парсинга body. Зеркало `toolBlacklist.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchChecklist(
  repoFullName: string,
): Promise<readonly ChecklistItem[]> {
  const query = new URLSearchParams({ repo_full_name: repoFullName });
  const response = await authedFetch(
    `/api/v1/reviewer_checklist?${query.toString()}`,
    { headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchChecklist: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as readonly ChecklistItem[];
}

export async function addChecklistItem(
  body: AddChecklistItemRequest,
): Promise<ChecklistItem> {
  const response = await authedFetch("/api/v1/reviewer_checklist", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `addChecklistItem: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ChecklistItem;
}

export async function removeChecklistItem(itemId: string): Promise<void> {
  const response = await authedFetch(
    `/api/v1/reviewer_checklist/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `removeChecklistItem: HTTP ${String(response.status)}`,
    );
  }
}
